import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Loader } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrepaidSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get('session_id');
  const [state, setState] = useState('verifying'); // verifying, success, failed
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) { setState('failed'); setError('Missing session ID'); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/prepaid/verify/${sessionId}`);
        if (cancelled) return;
        if (res.data?.fulfilment) {
          setData(res.data);
          setState('success');
          return;
        }
        if (res.data?.pending && attempts < 8) {
          setTimeout(poll, 2000);
        } else if (attempts >= 8) {
          setState('failed');
          setError('Payment not confirmed yet. Please contact support if you were charged.');
        } else if (res.data?.payment_status === 'paid') {
          setData(res.data);
          setState('success');
        }
      } catch (e) {
        if (cancelled) return;
        setState('failed');
        setError(e.response?.data?.detail || 'Could not verify payment');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="pt-20 min-h-screen" data-testid="prepaid-success-page">
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        {state === 'verifying' && (
          <div>
            <Loader className="w-12 h-12 text-uf-red mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-3xl font-bold text-white mb-3">Confirming your membership…</h1>
            <p className="text-uf-textMuted">Just a moment while Stripe confirms your payment.</p>
          </div>
        )}
        {state === 'success' && data && (
          <div data-testid="prepaid-success-panel">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-white" />
            </div>
            <h1 className="font-heading text-4xl font-bold text-white mb-3">Welcome to UltraFlex</h1>
            <p className="text-uf-textMuted mb-8">Your {data.fulfilment?.months}-month prepaid membership is active.</p>
            <div className="bg-uf-surface border border-uf-border p-6 text-left mb-6">
              <div className="flex justify-between py-2 border-b border-uf-border"><span className="text-uf-textMuted">Member number</span><span className="text-white font-mono">{data.fulfilment?.member_number}</span></div>
              <div className="flex justify-between py-2 border-b border-uf-border"><span className="text-uf-textMuted">Start date</span><span className="text-white">{data.fulfilment?.start_date}</span></div>
              <div className="flex justify-between py-2 border-b border-uf-border"><span className="text-uf-textMuted">Expires</span><span className="text-white">{data.fulfilment?.end_date}</span></div>
              <div className="flex justify-between py-2 border-b border-uf-border"><span className="text-uf-textMuted">Order ref</span><span className="text-white font-mono text-xs">{data.order_number}</span></div>
              <div className="flex justify-between py-2"><span className="text-uf-textMuted">Paid</span><span className="text-white font-bold">£{data.fulfilment?.total_paid?.toFixed(2)}</span></div>
            </div>
            <p className="text-uf-textMuted text-sm mb-6">We've emailed your welcome pack and PAR-Q to complete. Check your inbox to set your password and activate your digital access pass.</p>
            <Link to="/login" className="btn-primary inline-block"><span>Sign In to Member Hub</span></Link>
          </div>
        )}
        {state === 'failed' && (
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-3">We hit a snag</h1>
            <p className="text-uf-textMuted mb-6">{error}</p>
            <Link to="/membership" className="btn-primary inline-block"><span>Try again</span></Link>
          </div>
        )}
      </div>
    </div>
  );
}
